/**
 * GET /v1/athletics-hub/teams/:sectionPublicId — tiered team-detail payload.
 *
 * The public-leaning payload (team summary, schedule split into upcoming/past,
 * per-game scores via the reused `SchoolEvent.opponents`, off-season hero
 * fallback) is ALWAYS present to any school member. The `private_roster` block
 * is OPTIONAL — present only when the server resolved the viewer as a team
 * member / coach / admin (KTD3). On a non-member the block is omitted and the
 * page renders half, never errors.
 *
 * Wire-exclusion (KTD4): the schemas are `.strict()`, so a response carrying
 * `directions` (HTML/XSS) or internal `metadata` (BB ids) FAILS validation.
 * Scores are per-game only — no aggregate win-loss `record` field (KTD5).
 *
 * See: docs/_archive/2026-10-17-plans-sweep/plans/2026-06-18-004-feat-athletics-hub-front-door-plan.md (U1)
 */

import { z } from 'zod'
import { SchoolEventSchema } from '../../schemas/entities/school-event'
import { AthleticsHubTeamSummarySchema } from './common'

/**
 * One roster member in the member-only private block. Canonical
 * `SectionMembership`-sourced. Member-facing identity only — no contact PII
 * beyond display name (and `.strict()` guards accidental widening).
 */
export const AthleticsHubRosterMemberSchema = z
  .object({
    /** Display name. */
    name: z.string(),
    /** Section role (e.g. "STUDENT", "TEACHER"). Free-text from the source. */
    role: z.string().nullable().optional()
  })
  .strict()
export type AthleticsHubRosterMember = z.infer<typeof AthleticsHubRosterMemberSchema>

/**
 * The member-only private block. Present on the response only when the viewer
 * passed the per-team membership gate.
 */
export const AthleticsHubPrivateRosterSchema = z
  .object({
    members: z.array(AthleticsHubRosterMemberSchema)
  })
  .strict()
export type AthleticsHubPrivateRoster = z.infer<typeof AthleticsHubPrivateRosterSchema>

/**
 * The current-season date window for a team, when derivable from the school's
 * current school year. Both bounds are ISO-8601 timestamps. Clients use it to
 * bound the off-season fallback so a prior-season game is never surfaced as
 * "most recent" (review #1/#6). Omitted when the school has no current-year row;
 * clients then fall open / apply their own recency floor.
 */
export const AthleticsHubSeasonSchema = z
  .object({
    /** Inclusive season start (ISO-8601) — the current school year's begin date. */
    start: z.string(),
    /** Exclusive season end (ISO-8601) — the current school year's end date. */
    end: z.string()
  })
  .strict()
export type AthleticsHubSeason = z.infer<typeof AthleticsHubSeasonSchema>

/**
 * Tiered team-detail response. Public payload always present; `private_roster`
 * optional (tier-optionality encoded in the type). `.strict()` enforces
 * wire-exclusion of `directions` / internal `metadata`.
 */
export const AthleticsHubTeamResponseSchema = z
  .object({
    /** Public-leaning team identity (coach name + role only). */
    team: AthleticsHubTeamSummarySchema,
    /**
     * Current-season window (review #1/#6). Present when the school has a
     * current-year row; lets clients bound the off-season fallback. Omitted when
     * no current year is resolvable.
     */
    season: AthleticsHubSeasonSchema.optional(),
    /** Upcoming games (chronological). Reused source-agnostic SchoolEvent shape. */
    upcoming_games: z.array(SchoolEventSchema),
    /** Past games (reverse-chronological). Per-game scores ride on `opponents` (KTD5). */
    past_games: z.array(SchoolEventSchema),
    /**
     * Next-game hero, or null in the off-season (cancelled games skipped when
     * selecting). When null, clients fall back to the most-recent past game.
     */
    next_game: SchoolEventSchema.nullable().optional(),
    /**
     * Member-only roster. Present ONLY when the server resolved team membership;
     * omitted entirely otherwise (KTD3). Optional encodes the tier split.
     */
    private_roster: AthleticsHubPrivateRosterSchema.optional()
  })
  .strict()
export type AthleticsHubTeamResponse = z.infer<typeof AthleticsHubTeamResponseSchema>
